import React from 'react';

import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { disallowedVisitorTranslationMap, spaceTypeTranslationMap } from 'src/features/spaces/utils/constants';
import { BulkSpaceFormSchemaValues } from 'src/features/spaces/utils/formSchema';
import { spacesMessages } from 'src/features/spaces/utils/messages';
import { DISALLOW_USAGE_FOR, SPACE_TYPE } from 'src/features/spaces/utils/types';
import { useListZones } from 'src/features/zone/api/useListZones';
import { useParking } from 'src/providers/ParkingProvider';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { Form, FormControl, FormControlStack, FormItem, FormLabel, FormMessage, FormField } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { translate } from 'src/utils/translate';

type BulkSpaceFormProps = {
    form: UseFormReturn<BulkSpaceFormSchemaValues>;
    onSubmit: (values: BulkSpaceFormSchemaValues) => void;
};

function BulkSpaceForm({ onSubmit, form }: BulkSpaceFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { data: zonesData } = useListZones({ parkingId });

    const options = React.useMemo(() => {
        if (!zonesData) return [];
        return zonesData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [locale, zonesData]);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="start"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...spacesMessages.startNumber} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    step="1"
                                    min={0}
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) =>
                                        field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                                    }
                                    placeholder={`${formatMessage(spacesMessages.startNumber)}...`}
                                    onKeyDown={(e) => {
                                        if (e.key === '-' || e.key === '+' || e.key === 'e') {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="end"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage {...spacesMessages.endNumber} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    step="1"
                                    min={0}
                                    {...field}
                                    value={field.value ?? ''}
                                    onChange={(e) =>
                                        field.onChange(e.target.value === '' ? undefined : Number(e.target.value))
                                    }
                                    placeholder={`${formatMessage(spacesMessages.endNumber)}...`}
                                    onKeyDown={(e) => {
                                        if (e.key === '-' || e.key === '+' || e.key === 'e') {
                                            e.preventDefault();
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<BulkSpaceFormSchemaValues>
                form={form}
                name="zone"
                label={formatMessage(spacesMessages.zone)}
                options={options}
            />
            <SelectFormField<BulkSpaceFormSchemaValues>
                form={form}
                name="type"
                label={formatMessage(spacesMessages.type)}
                options={Object.values(SPACE_TYPE)}
                optionTranslationMap={spaceTypeTranslationMap}
            />
            <SelectFormField<BulkSpaceFormSchemaValues>
                form={form}
                name="disallow_usage_for"
                label={formatMessage(spacesMessages.disallowedVisitorCategories)}
                options={Object.values(DISALLOW_USAGE_FOR)}
                optionTranslationMap={disallowedVisitorTranslationMap}
            />
        </Form>
    );
}

export default BulkSpaceForm;
